<template>
    <section
        id="timesheet-assessment-readiness"
        class="timesheet-assessment-readiness"
        :class="{'is-ready': ready}"
        aria-labelledby="timesheet-assessment-readiness-title"
    >
        <header class="timesheet-assessment-readiness__header">
            <div class="timesheet-assessment-readiness__icon" aria-hidden="true">
                <i :class="ready ? 'fas fa-check' : 'fas fa-user-check'"></i>
            </div>
            <div class="timesheet-assessment-readiness__summary">
                <strong id="timesheet-assessment-readiness-title">КТУ и характеристики</strong>
                <span>
                    Готово {{ readyCount }} из {{ totalCount }}.
                    {{ ready ? 'Оценки заполнены.' : 'Нажмите на сотрудника, чтобы дополнить.' }}
                </span>
                <div class="timesheet-assessment-readiness__progress" aria-hidden="true">
                    <span :style="{width: progressWidth}"></span>
                </div>
            </div>
            <b-button
                v-if="!ready"
                size="sm"
                variant="outline-primary"
                @click="$emit('open-first')"
            >
                Заполнить
            </b-button>
        </header>
        <details class="timesheet-assessment-readiness__details">
        <summary>{{ ready ? 'Как оценивать' : `Подробнее · осталось ${totalCount - readyCount}` }}</summary>
        <p class="timesheet-assessment-readiness__rule">
            <strong>4 — норма.</strong>
            КТУ оценивает качество работы, а не количество дней на объекте.
            Нельзя занижать оценку только потому, что сотрудник работал мало дней.
            Для рабочих характеристика обязательна. Только ИТР могут быть без характеристики.
            Для полностью пустых строк оценки не нужны. Лишние пустые строки уберите из табеля перед отправкой.
        </p>
        <div v-if="issueGroups.length" class="timesheet-assessment-readiness__issues">
            <button
                v-for="issue in issueGroups.slice(0, 6)"
                :key="`assessment-issue-${issue.line_id}`"
                type="button"
                @click="$emit('open-issue', issue)"
            >
                <strong>{{ issue.employee_name }}</strong>
                <span>{{ issue.messages.join('; ') }}</span>
                <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
            <small v-if="issueGroups.length > 6">
                И ещё {{ issueGroups.length - 6 }}
                {{ pluralize(issueGroups.length - 6, 'сотрудник', 'сотрудника', 'сотрудников') }}.
            </small>
        </div>
        </details>
    </section>
</template>

<script>
export default {
    name: 'TimesheetAssessmentReadiness',
    props: {
        issueGroups: {type: Array, default: () => []},
        ready: {type: Boolean, default: false},
        readyCount: {type: Number, default: 0},
        totalCount: {type: Number, default: 0}
    },
    computed: {
        progressWidth() {
            return this.totalCount ? `${(this.readyCount / this.totalCount) * 100}%` : '100%';
        }
    },
    methods: {
        pluralize(count, one, few, many) {
            const value = Math.abs(Number(count)) % 100;
            const last = value % 10;
            if (value >= 11 && value <= 14) return many;
            if (last === 1) return one;
            if (last >= 2 && last <= 4) return few;
            return many;
        }
    }
};
</script>

<style scoped lang="scss">
.timesheet-assessment-readiness {
    margin: .75rem 0;
    padding: .8rem 1rem;
    border: 1px solid #f1c36d;
    border-left: 4px solid #e5a423;
    border-radius: 12px;
    background: #fffaf0;
}

.timesheet-assessment-readiness.is-ready {
    border-color: #a8d5b5;
    border-left-color: #2f855a;
    background: #f2fbf5;
}

.timesheet-assessment-readiness__header {
    display: flex;
    align-items: center;
    gap: .75rem;
}

.timesheet-assessment-readiness__icon {
    display: grid;
    place-items: center;
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    color: #8a5700;
    background: #ffedbd;
}

.is-ready .timesheet-assessment-readiness__icon {
    color: #276749;
    background: #d8f3df;
}

.timesheet-assessment-readiness__summary {
    flex: 1;
    min-width: 0;
}

.timesheet-assessment-readiness__summary strong,
.timesheet-assessment-readiness__summary span {
    display: block;
}

.timesheet-assessment-readiness__summary span {
    color: #5f6f7f;
    font-size: .78rem;
}

.timesheet-assessment-readiness__progress {
    height: 4px;
    margin-top: .4rem;
    overflow: hidden;
    border-radius: 999px;
    background: #eadfca;
}

.timesheet-assessment-readiness__progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: #d9930b;
    transition: width .2s ease;
}

.is-ready .timesheet-assessment-readiness__progress span {
    background: #2f855a;
}

.timesheet-assessment-readiness__rule {
    margin: .6rem 0 0 42px;
    color: #66552f;
    font-size: .76rem;
}

.timesheet-assessment-readiness__issues {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .35rem;
    margin: .65rem 0 0 42px;
}

.timesheet-assessment-readiness__issues > button {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: .1rem .5rem;
    padding: .45rem .55rem;
    border: 1px solid #e5c789;
    border-radius: 8px;
    color: #354a5f;
    background: #fff;
    text-align: left;
}

.timesheet-assessment-readiness__issues > button strong,
.timesheet-assessment-readiness__issues > button span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.timesheet-assessment-readiness__issues > button strong { font-size: .76rem; }
.timesheet-assessment-readiness__issues > button span { color: #7a6340; font-size: .68rem; }
.timesheet-assessment-readiness__issues > button i { grid-column: 2; grid-row: 1 / 3; align-self: center; }
.timesheet-assessment-readiness__issues > small { grid-column: 1 / -1; color: #7a6340; }

@media (max-width: 720px) {
    .timesheet-assessment-readiness { margin: .6rem 0; padding: .7rem; }
    .timesheet-assessment-readiness__header { align-items: flex-start; flex-wrap: wrap; }
    .timesheet-assessment-readiness__summary { width: calc(100% - 46px); }
    .timesheet-assessment-readiness__header .btn { width: 100%; margin-left: 42px; }
    .timesheet-assessment-readiness__rule,
    .timesheet-assessment-readiness__issues { margin-left: 0; }
    .timesheet-assessment-readiness__issues { grid-template-columns: 1fr; }
}
</style>
